import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, Globe, ArrowRight, Target, Rocket, Wrench, MessageSquare, Users } from "lucide-react";

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, fetchUser, api } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [businessName, setBusinessName] = useState(user?.business_name || "");
  const [businessWebsite, setBusinessWebsite] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  
  // Calendar mock state
  const [calendarBooked, setCalendarBooked] = useState(false);
  const iframeLoadedRef = React.useRef(false);
  const lastHeightRef = React.useRef(0);

  // Detect LeadConnector booking confirmation via multiple signals
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      
      // Signal 1: String messages containing booking keywords
      if (typeof data === 'string') {
        const lower = data.toLowerCase();
        if (lower.includes('booked') || lower.includes('confirmed') || lower.includes('appointment_created') || lower.includes('submission')) {
          setCalendarBooked(true);
          return;
        }
        // LeadConnector form_embed sends resize messages — a big height drop after load = confirmation page
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'resize' && iframeLoadedRef.current && parsed.height) {
            if (lastHeightRef.current > 400 && parsed.height < lastHeightRef.current * 0.6) {
              setCalendarBooked(true);
              return;
            }
            lastHeightRef.current = parsed.height;
          }
        } catch {}
      }
      
      // Signal 2: Object messages
      if (typeof data === 'object' && data !== null) {
        if (data.type === 'booking-confirmed' || data.action === 'booking_confirmed' || data.booked || data.event === 'formSubmit') {
          setCalendarBooked(true);
          return;
        }
        if (data.type === 'resize' && iframeLoadedRef.current && data.height) {
          if (lastHeightRef.current > 400 && data.height < lastHeightRef.current * 0.6) {
            setCalendarBooked(true);
            return;
          }
          lastHeightRef.current = data.height;
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    const timer = setTimeout(() => { iframeLoadedRef.current = true; }, 5000);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };
  }, []);

  const showStep2 = user?.plan === "agency" || user?.plan === "pro";
  const totalSteps = showStep2 ? 2 : 1;

  useEffect(() => {
    if (user?.onboarding_completed) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  const handleStep1Submit = async () => {
    if (!businessName.trim()) return;
    if (!showStep2) {
      await saveOnboarding();
    } else {
      setStep(2);
    }
  };

  const saveOnboarding = async (skipCall = false) => {
    setSaving(true);
    try {
      await api.post("/api/auth/onboarding", {
        business_name: businessName.trim(),
        business_website: businessWebsite.trim(),
        target_audience: targetAudience.trim(),
        booked_call: !skipCall && calendarBooked ? { booked_via: "ghl_calendar" } : null,
      });
      await fetchUser();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Onboarding save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  // Auto-redirect after LeadConnector booking
  useEffect(() => {
    if (calendarBooked) {
      saveOnboarding(false);
    }
  }, [calendarBooked]);

  // Load LeadConnector form embed script immediately and on step 2
  useEffect(() => {
    const loadScript = () => {
      const existing = document.querySelector('script[src="https://links.skyeai.io/js/form_embed.js"]');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.src = 'https://links.skyeai.io/js/form_embed.js';
      script.type = 'text/javascript';
      document.body.appendChild(script);
    };
    if (step === 2) {
      // Small delay to ensure iframe is in DOM first
      const timer = setTimeout(loadScript, 300);
      return () => clearTimeout(timer);
    }
  }, [step]);

  return (
    <div className="min-h-screen bg-[#07070b] flex items-center justify-center p-4 relative overflow-hidden" data-testid="onboarding-page">
      {/* Ambient glow */}
      <div className="fixed top-[-30%] left-[20%] w-[500px] h-[500px] bg-teal-500/[0.04] rounded-full blur-[120px] pointer-events-none" />
      <div className="fixed bottom-[-20%] right-[10%] w-[400px] h-[400px] bg-cyan-500/[0.03] rounded-full blur-[100px] pointer-events-none" />
      
      {/* Dot grid */}
      <div className="fixed inset-0 opacity-[0.025]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 0.5px, transparent 0)", backgroundSize: "32px 32px" }} />
      
      <div className="relative z-10 w-full max-w-5xl">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img src="/voxinity-logo-light-new.png" alt="Voxinity AI" className="h-9 object-contain" />
        </div>

        {/* Progress bar */}
        <div className="flex items-center justify-center gap-2.5 mb-10">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div key={i} className={`h-[3px] rounded-full transition-all duration-700 ease-out ${i + 1 <= step ? "w-14 bg-gradient-to-r from-teal-400 to-cyan-400" : "w-14 bg-white/[0.08]"}`} />
          ))}
        </div>

        {/* ═══ STEP 1 ═══ */}
        {step === 1 && (
          <div className="max-w-md mx-auto" data-testid="onboarding-step-1" style={{ animation: "fadeUp 0.5s ease-out" }}>
            <div className="text-center mb-9">
              <h1 className="text-[28px] sm:text-[34px] font-semibold text-white tracking-[-0.02em] mb-2.5">
                Let's set up your account
              </h1>
              <p className="text-[14px] text-white/40 leading-relaxed">
                Tell us about your business so we can personalize your experience.
              </p>
            </div>

            <div className="space-y-4">
              {/* Business Name */}
              <div className="group">
                <label className="block text-[11px] font-semibold text-white/40 mb-2 uppercase tracking-[0.12em]">Business Name</label>
                <div className="relative">
                  <Building2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-teal-400/60 transition-colors" />
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Your business name"
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.03] border border-white/[0.07] rounded-xl text-white placeholder-white/20 text-[14px] focus:outline-none focus:border-teal-400/30 focus:bg-white/[0.05] focus:shadow-[0_0_20px_rgba(45,212,191,0.05)] transition-all duration-300"
                    data-testid="business-name-input"
                    autoFocus
                  />
                </div>
              </div>

              {/* Website URL */}
              <div className="group">
                <label className="block text-[11px] font-semibold text-white/40 mb-2 uppercase tracking-[0.12em]">
                  Website URL <span className="text-white/20 normal-case tracking-normal font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Globe size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-teal-400/60 transition-colors" />
                  <input
                    type="url"
                    value={businessWebsite}
                    onChange={(e) => setBusinessWebsite(e.target.value)}
                    placeholder="https://yourbusiness.com"
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.03] border border-white/[0.07] rounded-xl text-white placeholder-white/20 text-[14px] focus:outline-none focus:border-teal-400/30 focus:bg-white/[0.05] focus:shadow-[0_0_20px_rgba(45,212,191,0.05)] transition-all duration-300"
                    data-testid="business-website-input"
                  />
                </div>
              </div>

              {/* Target Audience / Industry */}
              <div className="group">
                <label className="block text-[11px] font-semibold text-white/40 mb-2 uppercase tracking-[0.12em]">
                  Target Audience / Industry <span className="text-white/20 normal-case tracking-normal font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Users size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-teal-400/60 transition-colors" />
                  <input
                    type="text"
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    placeholder="e.g., Real Estate, Coaching, Dental"
                    className="w-full pl-11 pr-4 py-3 bg-white/[0.03] border border-white/[0.07] rounded-xl text-white placeholder-white/20 text-[14px] focus:outline-none focus:border-teal-400/30 focus:bg-white/[0.05] focus:shadow-[0_0_20px_rgba(45,212,191,0.05)] transition-all duration-300"
                    data-testid="target-audience-input"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleStep1Submit}
              disabled={!businessName.trim() || saving}
              className="w-full mt-7 py-3 bg-gradient-to-r from-teal-500 to-teal-400 hover:from-teal-400 hover:to-cyan-400 disabled:opacity-20 disabled:cursor-not-allowed text-[#07070b] font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-[14px] shadow-[0_4px_24px_rgba(45,212,191,0.15)] hover:shadow-[0_4px_32px_rgba(45,212,191,0.25)]"
              data-testid="onboarding-continue-btn"
            >
              {saving ? "Setting up..." : showStep2 ? "Continue" : "Get Started"}
              {!saving && <ArrowRight size={16} strokeWidth={2.5} />}
            </button>
          </div>
        )}

        {/* ═══ STEP 2 ═══ */}
        {step === 2 && (
          <div data-testid="onboarding-step-2" style={{ animation: "fadeUp 0.5s ease-out" }}>
            <div className="text-center mb-9">
              <h1 className="text-[28px] sm:text-[34px] font-semibold text-white tracking-[-0.02em] mb-2.5">
                Book your free roadmap session
              </h1>
              <p className="text-[14px] text-white/40 leading-relaxed">
                Let us help you map out your AI voice strategy. 15 minutes, zero fluff.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 items-start">
              {/* Left: Value Prop */}
              <div className="lg:w-[40%] space-y-5">
                <div className="space-y-4">
                  {[
                    { icon: Target, title: "Walk through your current workflow", desc: "We'll understand your business process and identify automation opportunities." },
                    { icon: Rocket, title: "Build a custom automation roadmap", desc: "Get a tailored plan specific to your business needs and goals." },
                    { icon: Wrench, title: "Get hands-on help with your first AI agent", desc: "We'll guide you through setting up and launching your first voice agent." },
                    { icon: MessageSquare, title: "Leave with a clear action plan", desc: "Walk away with concrete next steps — no strings attached." },
                  ].map(({ icon: Icon, title, desc }, idx) => (
                    <div key={title} className="flex gap-3.5 group" style={{ animation: `fadeUp 0.4s ease-out ${idx * 0.08}s both` }}>
                      <div className="w-8 h-8 rounded-lg bg-teal-400/[0.08] border border-teal-400/[0.12] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-teal-400/[0.14] transition-colors">
                        <Icon size={15} className="text-teal-400/80" />
                      </div>
                      <div>
                        <h3 className="text-[13px] font-semibold text-white/90 mb-0.5 leading-snug">{title}</h3>
                        <p className="text-[12px] text-white/35 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t border-white/[0.05]">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-white/25 font-semibold mb-2.5">What you'll walk away with</p>
                  <div className="flex flex-wrap gap-1.5">
                    {["Custom roadmap", "First agent setup", "Workflow analysis", "Action plan"].map(tag => (
                      <span key={tag} className="px-2.5 py-1 bg-teal-400/[0.06] border border-teal-400/[0.12] rounded-full text-[11px] text-teal-300/70 font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: LeadConnector Calendar Embed */}
              <div className="lg:w-[60%] w-full">
                <div 
                  className="rounded-2xl bg-white/[0.02] border border-white/[0.07]"
                  style={{ minHeight: '600px' }}
                  dangerouslySetInnerHTML={{
                    __html: `
                      <iframe 
                        src="https://links.skyeai.io/widget/booking/UTSmNKx10xCIHjDQ6Zfe" 
                        style="width: 100%; min-height: 600px; border: none; overflow: hidden;" 
                        scrolling="no" 
                        id="UTSmNKx10xCIHjDQ6Zfe_1776036765905"
                        data-testid="ghl-calendar-embed"
                        title="Book a Roadmap Session"
                      ></iframe>
                    `
                  }}
                />

                {/* Continue after booking — only shows when booked */}
                {calendarBooked && (
                  <button
                    onClick={() => saveOnboarding(false)}
                    disabled={saving}
                    className="w-full mt-4 py-2.5 bg-gradient-to-r from-teal-500 to-teal-400 hover:from-teal-400 hover:to-cyan-400 disabled:opacity-40 text-[#07070b] font-semibold rounded-xl transition-all duration-300 text-[13px] flex items-center justify-center gap-2 shadow-[0_4px_24px_rgba(45,212,191,0.15)]"
                    data-testid="continue-after-booking-btn"
                    style={{ animation: "fadeUp 0.4s ease-out" }}
                  >
                    {saving ? "Saving..." : "Booked! Continue to Dashboard"}
                    {!saving && <ArrowRight size={15} strokeWidth={2.5} />}
                  </button>
                )}

                {/* Skip — hidden once booked */}
                {!calendarBooked && (
                  <button
                    onClick={() => saveOnboarding(true)}
                    disabled={saving}
                    className="w-full mt-4 text-[12px] text-white/20 hover:text-white/40 transition-colors duration-300 text-center"
                    data-testid="skip-onboarding-link"
                  >
                    I'll schedule later, take me to the dashboard
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default OnboardingPage;
